import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _to_property_key } from "@swc/helpers/_/_to_property_key";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { Component } from 'react';
import { TableListContext } from './TableList.utils';
/**
 * Provides configuration to a non-grid table.
 * Implemented as a class component to promote stability even when callback props change their reference.
 */ export class TableListProvider extends Component {
    componentDidUpdate(prevProps) {
        const { sort, selectedRows, columnDefs, numRows, numSelectedRows, onToggleAllSelection } = this.props;
        // Makes sure the internal state reflects the provided sort prop.
        if (sort !== prevProps.sort) {
            this.setState((s)=>({
                    value: _object_spread_props(_object_spread({}, s.value), {
                        sort
                    })
                }));
        }
        // Makes sure the internal state reflects the provided selectedRows prop.
        if (selectedRows !== prevProps.selectedRows && selectedRows) {
            this.setState((s)=>({
                    value: _object_spread_props(_object_spread({}, s.value), {
                        selectedRows
                    })
                }));
        }
        if (columnDefs !== prevProps.columnDefs) {
            this.setState((s)=>({
                    value: _object_spread_props(_object_spread({}, s.value), {
                        columnDefs
                    })
                }));
        }
        if (numRows !== prevProps.numRows) {
            this.setState((s)=>({
                    value: _object_spread_props(_object_spread({}, s.value), {
                        numRows
                    })
                }));
        }
        if (numSelectedRows !== prevProps.numSelectedRows) {
            this.setState((s)=>({
                    value: _object_spread_props(_object_spread({}, s.value), {
                        numSelectedRows
                    })
                }));
        }
        if (onToggleAllSelection !== prevProps.onToggleAllSelection) {
            this.setState((s)=>({
                    value: _object_spread_props(_object_spread({}, s.value), {
                        onToggleAllSelection
                    })
                }));
        }
    }
    render() {
        return /*#__PURE__*/ _jsx(TableListContext.Provider, {
            value: this.state.value,
            children: this.props.children
        });
    }
    constructor(...args){
        var _this_props_variant;
        super(...args), _define_property(this, "registerRow", (param)=>{
            let { rowId, isExpanded } = param;
            this.setState((s)=>({
                    value: _object_spread_props(_object_spread({}, s.value), {
                        expandedRows: _object_spread_props(_object_spread({}, s.value.expandedRows), {
                            [rowId]: isExpanded
                        })
                    })
                }));
        }), _define_property(this, "unregisterRow", (rowId)=>{
            this.setState((s)=>{
                const _s_value_expandedRows = s.value.expandedRows, { [rowId]: _ } = _s_value_expandedRows, remainingRows = _object_without_properties(_s_value_expandedRows, [
                    _to_property_key(rowId)
                ]);
                return {
                    value: _object_spread_props(_object_spread({}, s.value), {
                        expandedRows: remainingRows
                    })
                };
            });
        }), _define_property(this, "expandAllRows", (isAllExpanded)=>{
            const { expandedRows } = this.state.value;
            if (!expandedRows) return;
            Object.keys(expandedRows).forEach((key)=>{
                expandedRows[key] = isAllExpanded;
            });
            this.setState({
                value: _object_spread_props(_object_spread({}, this.state.value), {
                    expandedRows
                })
            });
        }), _define_property(this, "toggleExpandedRow", (rowId)=>{
            const { expandedRows } = this.state.value;
            const rowExpanded = !expandedRows[rowId];
            if (this.props.singleExpandOnly && rowExpanded) {
                Object.keys(expandedRows).forEach((key)=>{
                    expandedRows[key] = false;
                });
            }
            this.setState({
                value: _object_spread_props(_object_spread({}, this.state.value), {
                    expandedRows: _object_spread_props(_object_spread({}, expandedRows), {
                        [rowId]: rowExpanded
                    })
                })
            });
        }), _define_property(this, "setSort", (newSort)=>{
            const { onSortChange } = this.props;
            const { sort } = this.state.value;
            const dataChanged = (sort === null || sort === void 0 ? void 0 : sort.colIndex) !== (newSort === null || newSort === void 0 ? void 0 : newSort.colIndex) || (sort === null || sort === void 0 ? void 0 : sort.sort) !== (newSort === null || newSort === void 0 ? void 0 : newSort.sort);
            if (dataChanged) {
                onSortChange && onSortChange(newSort);
                this.setState({
                    value: _object_spread_props(_object_spread({}, this.state.value), {
                        sort: newSort
                    })
                });
            }
        }), _define_property(this, "setSelectedRows", (changes)=>{
            const { onSelectionChange } = this.props;
            const { selectedRows } = this.state.value;
            let dataChanged = false;
            Object.keys(changes).forEach((id)=>{
                const isSelected = changes[id];
                if (selectedRows[id] !== isSelected) {
                    selectedRows[id] = isSelected;
                    dataChanged = true;
                }
            });
            if (dataChanged) {
                const newSelectedRows = _object_spread({}, selectedRows);
                onSelectionChange && onSelectionChange(newSelectedRows, changes);
                this.setState({
                    value: _object_spread_props(_object_spread({}, this.state.value), {
                        selectedRows: newSelectedRows
                    })
                });
            }
        }), _define_property(this, "state", {
            value: {
                columnDefs: this.props.columnDefs,
                expandableRows: this.props.expandableRows,
                expandableRowsVariant: this.props.expandableRowsVariant,
                expandedRows: this.props.initialExpandedRows || {},
                enableRowSelection: this.props.enableRowSelection,
                selectedRows: this.props.selectedRows || {},
                sort: this.props.sort,
                variant: (_this_props_variant = this.props.variant) !== null && _this_props_variant !== void 0 ? _this_props_variant : 'default',
                hasActionColumn: this.props.hasActionColumn,
                numRows: this.props.numRows,
                numSelectedRows: this.props.numSelectedRows,
                hPadding: this.props.hPadding || 'm',
                justifyBetween: this.props.justifyBetween || false,
                disableMobile: this.props.disableMobile,
                onToggleAllSelection: this.props.onToggleAllSelection,
                toggleExpandedRow: this.toggleExpandedRow,
                setSelectedRows: this.setSelectedRows,
                setSort: this.setSort,
                expandAllRows: this.expandAllRows,
                registerRow: this.registerRow,
                unregisterRow: this.unregisterRow
            }
        });
    }
}
